import { SaveOutlined } from '@mui/icons-material'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import { useAppController } from 'customHooks/useAppController'
import { WorkingStepsFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/WorkingStepsFilter'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { BomItemType } from 'model/Project/BoMItemRow'
import { useCallback, useState } from 'react'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import store from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQLines, RFQLine } from '../Components/ProjectRFQLines'
import { ProjectRFQController } from '../ProjectRFQController'

export type DraftRFQDialogProps = {
  isOpen: boolean
  onClose: () => void
}

export const DraftRFQDialog = (props: DraftRFQDialogProps) => {
  const { controller: projectRFQController, loading: projectRFQLoading } =
    useAppController(() => new ProjectRFQController())

  const [draftRFQLines, setDraftRFQLines] = useState<RFQLine[]>([])
  const [selectedWorkingSteps, setSelectedWorkingSteps] = useState<
    WorkingStepType[]
  >([])

  const ShowException = useShowException('project suppliers')

  const { onClose } = props

  const handleCreateRequestForQuotation = useCallback(async () => {
    try {
      await projectRFQController.createRequestForQuotation(draftRFQLines)

      onClose()
    } catch (err) {
      ShowException(err)
    }
  }, [ShowException, draftRFQLines, onClose, projectRFQController])

  const applySelectedWorkingSteps = useCallback(() => {
    const projectState = store.getState().project

    if (!projectState) return

    const selectedWorkingStepSet = new Set(selectedWorkingSteps)
    const filteredAssemblyHeaders = projectState.filteredAssemblyHeaderIds

    // use filtered assemblies and parttypes to generate the draft RFQ
    const newRFQLines: RFQLine[] = []

    for (const assemblyId of filteredAssemblyHeaders) {
      if (
        projectState.assemblyHeaders[assemblyId].assemblyActivities.some((x) =>
          selectedWorkingStepSet.has(x.primaryWorkingStep),
        )
      ) {
        const existingLine = draftRFQLines.find(
          (line) => line.bomItemPointer.id === assemblyId,
        )
        newRFQLines.push({
          bomItemPointer: {
            id: assemblyId,
            type: BomItemType.assemblyType,
          },
          activities: projectState.assemblyHeaders[
            assemblyId
          ].assemblyActivities
            .filter((x) => selectedWorkingStepSet.has(x.primaryWorkingStep))
            .map((x) => ({
              activityId: x.id,
              workingStepType: x.primaryWorkingStep,
              selected:
                existingLine?.activities?.find(
                  (activity) => activity.activityId === x.id,
                )?.selected ?? true,
            })),
          supplier: null,
        })
      }

      const partTypes =
        projectState.assemblyHeaders[assemblyId].filteredPartTypePointers

      for (const partTypePointer of partTypes) {
        const partType = projectState.partTypes[partTypePointer.id]
        if (
          partType.activities.some((x) =>
            selectedWorkingStepSet.has(x.primaryWorkingStep),
          )
        ) {
          const existingLine = draftRFQLines.find(
            (line) =>
              line.bomItemPointer.id === partTypePointer.id &&
              line.bomItemPointer.type === BomItemType.partType,
          )
          newRFQLines.push({
            bomItemPointer: {
              id: partTypePointer.id,
              type: BomItemType.partType,
            },
            activities: partType.activities
              .filter((x) => selectedWorkingStepSet.has(x.primaryWorkingStep))
              .map((x) => ({
                activityId: x.id,
                workingStepType: x.primaryWorkingStep,
                selected:
                  existingLine?.activities?.find(
                    (activity) => activity.activityId === x.id,
                  )?.selected ?? true,
              })),
            supplier: null, // supplier will be determined later based on activities
          })
        }
      }
    }

    setDraftRFQLines(newRFQLines)
  }, [draftRFQLines, selectedWorkingSteps])

  const handleWorkingStepFilterChange = useCallback(
    (workingStepType: WorkingStepType | null) => {
      if (!workingStepType) return

      setSelectedWorkingSteps((current) =>
        current.includes(workingStepType)
          ? current.filter((item) => item !== workingStepType)
          : [...current, workingStepType],
      )
    },
    [],
  )

  const [selectedBomItemIds, setSelectedBomItemIds] = useState<Set<string>>(
    new Set(),
  )

  const handleDeleteSelectedRows = useCallback(() => {
    if (selectedBomItemIds.size === 0) return

    const updatedLines = draftRFQLines.filter(
      (line) => !selectedBomItemIds.has(line.bomItemPointer.id),
    )

    setDraftRFQLines(updatedLines)
    setSelectedBomItemIds(new Set())
  }, [draftRFQLines, selectedBomItemIds])

  const handleLineActivitySelectionChange = useCallback(
    (bomItemId: string, activityIndex: number, selected: boolean) => {
      setDraftRFQLines((prev) =>
        prev.map((line) => {
          if (line.bomItemPointer.id !== bomItemId || !line.activities) {
            return line
          }

          return {
            ...line,
            activities: line.activities.map((activity, index) =>
              index === activityIndex ? { ...activity, selected } : activity,
            ),
          }
        }),
      )
    },
    [],
  )

  const linesWithoutSelectedActivities = draftRFQLines.filter(
    (line) => !(line.activities ?? []).some((activity) => activity.selected),
  ).length
  const canSave =
    draftRFQLines.length > 0 && linesWithoutSelectedActivities === 0

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="xxl"
      slotProps={{
        paper: { sx: { height: '85vh', maxHeight: '85vh' } },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Draft Request for Quotation
          </Typography>
          {draftRFQLines.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                label={`${draftRFQLines.length} items`}
              />
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', gap: 2, overflow: 'hidden', pt: 1 }}
      >
        {/* Left sidebar — Working Step Filter */}
        <Box
          sx={{
            minWidth: 220,
            maxWidth: 260,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            borderRight: 1,
            borderColor: 'divider',
            pr: 2,
            overflow: 'auto',
          }}
        >
          <Typography
            variant="caption"
            color="secondary"
            sx={{
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Filter by working steps
          </Typography>
          <WorkingStepsFilter
            selectedFilters={selectedWorkingSteps}
            onFilterChange={handleWorkingStepFilterChange}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<PlaylistAddIcon />}
            onClick={applySelectedWorkingSteps}
            disabled={selectedWorkingSteps.length === 0}
            sx={{ mt: 1 }}
          >
            Apply & load items
          </Button>
        </Box>

        {/* Right panel - canonical RFQ scope */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {draftRFQLines.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Typography variant="subtitle1" color="secondary">
                Select working steps and click "Apply" to add RFQ lines.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                overflow: 'auto',
              }}
            >
              <ProjectRFQLines
                rfqLines={draftRFQLines}
                onSelectionChange={(bomItemId, checked) => {
                  setSelectedBomItemIds((prev) => {
                    const newSet = new Set(prev)
                    if (checked) {
                      newSet.add(bomItemId)
                    } else {
                      newSet.delete(bomItemId)
                    }
                    return newSet
                  })
                }}
                selectedBomItemIds={selectedBomItemIds}
                onLineActivitySelectionChange={
                  handleLineActivitySelectionChange
                }
                onDeleteSelected={handleDeleteSelectedRows}
                selectionCount={selectedBomItemIds.size}
                showSupplierAssignment={false}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              projectRFQLoading['createRequestForQuotation'] ? (
                <CircularProgress size={20} />
              ) : (
                <SaveOutlined />
              )
            }
            disabled={
              !canSave || projectRFQLoading['createRequestForQuotation']
            }
            onClick={handleCreateRequestForQuotation}
          >
            Save RFQ
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export const OpenRFQDraftDialogButton = () => {
  const { openDialog } = useDialogManager()

  const handleOpenDialog = () => {
    openDialog('DraftRFQDialog')
  }

  return <Button onClick={handleOpenDialog}>Draft RFQ</Button>
}
